import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Production = () => {
  const [entries, setEntries] = useState([]);
  const [stock, setStock] = useState(null);
  const [dailyBalance, setDailyBalance] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [open, setOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    large_crates: '0',
    large_singles: '0',
    medium_crates: '0',
    medium_singles: '0',
    small_crates: '0',
    small_singles: '0',
    cracked_eggs: '0',
    notes: ''
  });

  const [adjustmentData, setAdjustmentData] = useState({
    date: new Date().toISOString().split('T')[0],
    adjustment_type: 'closing', // 'opening', 'production', 'sales', 'closing', 'manual'
    large_adjustment: '0',
    medium_adjustment: '0',
    small_adjustment: '0',
    cracked_adjustment: '0',
    // Target values for direct set mode
    target_large: '',
    target_medium: '',
    target_small: '',
    target_cracked: '',
    reason: ''
  });

  useEffect(() => {
    fetchEntries();
    fetchStock();
    fetchDailyBalance(selectedDate);
  }, [selectedDate]);

  const fetchDailyBalance = async (date) => {
    try {
      const response = await axios.get(`${API}/inventory/daily-balance/${date}`, getAuthHeader());
      setDailyBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch daily balance');
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/production`, getAuthHeader());
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch production entries');
    }
  };

  const fetchStock = async () => {
    try {
      const response = await axios.get(`${API}/production/stock`, getAuthHeader());
      setStock(response.data);
    } catch (error) {
      console.error('Failed to fetch stock data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await axios.put(`${API}/production/${editingEntry.id}`, formData, getAuthHeader());
        toast.success('Entry updated successfully');
      } else {
        await axios.post(`${API}/production`, formData, getAuthHeader());
        toast.success('Entry added successfully');
      }
      fetchEntries();
      fetchStock();
      fetchDailyBalance(selectedDate);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    try {
      let adjustmentPayload = {
        date: selectedDate,
        reason: adjustmentData.reason
      };

      if (adjustmentData.adjustment_type === 'manual') {
        // Manual adjustment - use the values directly
        adjustmentPayload.large_adjustment = parseInt(adjustmentData.large_adjustment) || 0;
        adjustmentPayload.medium_adjustment = parseInt(adjustmentData.medium_adjustment) || 0;
        adjustmentPayload.small_adjustment = parseInt(adjustmentData.small_adjustment) || 0;
        adjustmentPayload.cracked_adjustment = parseInt(adjustmentData.cracked_adjustment) || 0;
      } else {
        // Set to target value - calculate adjustment needed
        const targetLarge = adjustmentData.target_large !== '' ? parseInt(adjustmentData.target_large) : null;
        const targetMedium = adjustmentData.target_medium !== '' ? parseInt(adjustmentData.target_medium) : null;
        const targetSmall = adjustmentData.target_small !== '' ? parseInt(adjustmentData.target_small) : null;
        const targetCracked = adjustmentData.target_cracked !== '' ? parseInt(adjustmentData.target_cracked) : null;

        let currentValue = { large: 0, medium: 0, small: 0, cracked: 0 };
        
        if (adjustmentData.adjustment_type === 'opening' && dailyBalance) {
          currentValue = dailyBalance.opening_balance;
        } else if (adjustmentData.adjustment_type === 'production' && dailyBalance) {
          currentValue = dailyBalance.production;
        } else if (adjustmentData.adjustment_type === 'sales' && dailyBalance) {
          currentValue = dailyBalance.sales;
        } else if (adjustmentData.adjustment_type === 'closing' && dailyBalance) {
          currentValue = dailyBalance.closing_balance;
        }

        // Calculate adjustments needed to reach target
        adjustmentPayload.large_adjustment = targetLarge !== null ? targetLarge - currentValue.large : 0;
        adjustmentPayload.medium_adjustment = targetMedium !== null ? targetMedium - currentValue.medium : 0;
        adjustmentPayload.small_adjustment = targetSmall !== null ? targetSmall - currentValue.small : 0;
        adjustmentPayload.cracked_adjustment = targetCracked !== null ? targetCracked - currentValue.cracked : 0;
      }

      // Check if any adjustment is needed
      if (adjustmentPayload.large_adjustment === 0 && adjustmentPayload.medium_adjustment === 0 && 
          adjustmentPayload.small_adjustment === 0 && adjustmentPayload.cracked_adjustment === 0) {
        toast.info('No adjustment needed - values already match');
        return;
      }

      await axios.post(`${API}/inventory/adjustments`, adjustmentPayload, getAuthHeader());
      toast.success('Inventory adjusted successfully');
      fetchDailyBalance(selectedDate);
      fetchStock();
      setAdjustOpen(false);
      resetAdjustmentForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const resetAdjustmentForm = () => {
    setAdjustmentData({
      date: selectedDate,
      adjustment_type: 'closing',
      large_adjustment: '0',
      medium_adjustment: '0',
      small_adjustment: '0',
      cracked_adjustment: '0',
      target_large: '',
      target_medium: '',
      target_small: '',
      target_cracked: '',
      reason: ''
    });
  };

  const openAdjustDialog = () => {
    // Pre-fill with current closing balance values
    if (dailyBalance) {
      setAdjustmentData({
        ...adjustmentData,
        date: selectedDate,
        adjustment_type: 'closing',
        target_large: dailyBalance.closing_balance.large.toString(),
        target_medium: dailyBalance.closing_balance.medium.toString(),
        target_small: dailyBalance.closing_balance.small.toString(),
        target_cracked: dailyBalance.closing_balance.cracked.toString(),
        reason: ''
      });
    }
    setAdjustOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/production/${id}`, getAuthHeader());
      toast.success('Entry deleted successfully');
      fetchEntries();
      fetchStock();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      large_crates: entry.large_crates?.toString() || '0',
      large_singles: entry.large_singles?.toString() || '0',
      medium_crates: entry.medium_crates?.toString() || '0',
      medium_singles: entry.medium_singles?.toString() || '0',
      small_crates: entry.small_crates?.toString() || '0',
      small_singles: entry.small_singles?.toString() || '0',
      cracked_eggs: entry.cracked_eggs?.toString() || '0',
      notes: entry.notes || ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      large_crates: '0',
      large_singles: '0',
      medium_crates: '0',
      medium_singles: '0',
      small_crates: '0',
      small_singles: '0',
      cracked_eggs: '0',
      notes: ''
    });
  };

  const calculateTotalEggs = (entry) => {
    const largeCrates = parseInt(entry.large_crates || 0);
    const largeSingles = parseInt(entry.large_singles || 0);
    const mediumCrates = parseInt(entry.medium_crates || 0);
    const mediumSingles = parseInt(entry.medium_singles || 0);
    const smallCrates = parseInt(entry.small_crates || 0);
    const smallSingles = parseInt(entry.small_singles || 0);
    
    return (largeCrates * 30) + largeSingles + (mediumCrates * 30) + mediumSingles + (smallCrates * 30) + smallSingles;
  };

  return (
    <div className="space-y-6" data-testid="production-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daily Inventory
          </h1>
          <p className="text-lg text-slate-600 mt-2">Overall egg inventory including pen production</p>
        </div>
        {(user.role === 'admin' || user.role === 'manager') && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-production-button" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="production-dialog">
              <DialogHeader>
                <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Production Entry'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="production-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Large / Regular Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="large_crates">Crates (30 eggs)</Label>
                    <Input
                      id="large_crates"
                      type="number"
                      data-testid="large-crates-input"
                      value={formData.large_crates}
                      onChange={(e) => setFormData({ ...formData, large_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="large_singles">Singles</Label>
                    <Input
                      id="large_singles"
                      type="number"
                      data-testid="large-singles-input"
                      value={formData.large_singles}
                      onChange={(e) => setFormData({ ...formData, large_singles: e.target.value })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Medium Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="medium_crates">Crates (30 eggs)</Label>
                    <Input
                      id="medium_crates"
                      type="number"
                      data-testid="medium-crates-input"
                      value={formData.medium_crates}
                      onChange={(e) => setFormData({ ...formData, medium_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="medium_singles">Singles</Label>
                    <Input
                      id="medium_singles"
                      type="number"
                      data-testid="medium-singles-input"
                      value={formData.medium_singles}
                      onChange={(e) => setFormData({ ...formData, medium_singles: e.target.value })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Small Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="small_crates">Crates (30 eggs)</Label>
                    <Input
                      id="small_crates"
                      type="number"
                      data-testid="small-crates-input"
                      value={formData.small_crates}
                      onChange={(e) => setFormData({ ...formData, small_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="small_singles">Singles</Label>
                    <Input
                      id="small_singles"
                      type="number"
                      data-testid="small-singles-input"
                      value={formData.small_singles}
                      onChange={(e) => setFormData({ ...formData, small_singles: e.target.value })}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label htmlFor="cracked_eggs">Cracked Eggs</Label>
                <Input
                  id="cracked_eggs"
                  type="number"
                  data-testid="cracked-eggs-input"
                  value={formData.cracked_eggs}
                  onChange={(e) => setFormData({ ...formData, cracked_eggs: e.target.value })}
                  min="0"
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="production-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="production-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingEntry ? 'Update' : 'Add'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stock Summary Cards */}
      {stock && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="card p-4 bg-gradient-to-br from-emerald-50 to-white">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Large Eggs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stock.large_stock}</p>
            <p className="text-xs text-slate-500 mt-1">in stock</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-blue-50 to-white">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Medium Eggs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stock.medium_stock}</p>
            <p className="text-xs text-slate-500 mt-1">in stock</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-amber-50 to-white">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Small Eggs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stock.small_stock}</p>
            <p className="text-xs text-slate-500 mt-1">in stock</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-red-50 to-white">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Cracked Eggs</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stock.cracked_stock}</p>
            <p className="text-xs text-slate-500 mt-1">in stock</p>
          </div>
          <div className="card p-4 bg-gradient-to-br from-purple-50 to-white border-2 border-emerald-200">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Stock</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{stock.total_stock}</p>
            <p className="text-xs text-slate-500 mt-1">all sizes</p>
          </div>
        </div>
      )}

      {/* Daily Balance Section */}
      {dailyBalance && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>View Date:</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-48"
                />
                <span className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-2 rounded">
                  {formatDate(selectedDate)}
                </span>
              </div>
            </div>
            {(user.role === 'admin' || user.role === 'manager') && (
              <Button onClick={openAdjustDialog} variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50" data-testid="adjust-inventory-button">
                <Settings className="w-4 h-4 mr-2" />
                Adjust Inventory
              </Button>
            )}
          </div>

          <div className="card p-6">
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Daily Balance - {formatDate(selectedDate)}
            </h3>
            <div className="grid grid-cols-5 gap-4 text-sm">
              <div className="font-semibold">Category</div>
              <div className="font-semibold text-right">Large</div>
              <div className="font-semibold text-right">Medium</div>
              <div className="font-semibold text-right">Small</div>
              <div className="font-semibold text-right">Cracked</div>
              
              <div className="text-slate-600">Opening Balance</div>
              <div className="text-right">{dailyBalance.opening_balance.large}</div>
              <div className="text-right">{dailyBalance.opening_balance.medium}</div>
              <div className="text-right">{dailyBalance.opening_balance.small}</div>
              <div className="text-right">{dailyBalance.opening_balance.cracked}</div>
              
              <div className="text-emerald-600">+ Production</div>
              <div className="text-right text-emerald-600">+{dailyBalance.production.large}</div>
              <div className="text-right text-emerald-600">+{dailyBalance.production.medium}</div>
              <div className="text-right text-emerald-600">+{dailyBalance.production.small}</div>
              <div className="text-right text-emerald-600">+{dailyBalance.production.cracked}</div>
              
              <div className="text-red-600">- Sales</div>
              <div className="text-right text-red-600">-{dailyBalance.sales.large}</div>
              <div className="text-right text-red-600">-{dailyBalance.sales.medium}</div>
              <div className="text-right text-red-600">-{dailyBalance.sales.small}</div>
              <div className="text-right text-red-600">-{dailyBalance.sales.cracked}</div>
              
              {dailyBalance.adjustments.total !== 0 && (
                <>
                  <div className="text-amber-600">Adjustments</div>
                  <div className="text-right text-amber-600">{dailyBalance.adjustments.large > 0 ? '+' : ''}{dailyBalance.adjustments.large}</div>
                  <div className="text-right text-amber-600">{dailyBalance.adjustments.medium > 0 ? '+' : ''}{dailyBalance.adjustments.medium}</div>
                  <div className="text-right text-amber-600">{dailyBalance.adjustments.small > 0 ? '+' : ''}{dailyBalance.adjustments.small}</div>
                  <div className="text-right text-amber-600">{dailyBalance.adjustments.cracked > 0 ? '+' : ''}{dailyBalance.adjustments.cracked}</div>
                </>
              )}
              
              <div className="font-bold text-lg border-t pt-2">Closing Balance</div>
              <div className="text-right font-bold text-lg border-t pt-2">{dailyBalance.closing_balance.large}</div>
              <div className="text-right font-bold text-lg border-t pt-2">{dailyBalance.closing_balance.medium}</div>
              <div className="text-right font-bold text-lg border-t pt-2">{dailyBalance.closing_balance.small}</div>
              <div className="text-right font-bold text-lg border-t pt-2">{dailyBalance.closing_balance.cracked}</div>
            </div>
            <p className="text-sm text-slate-500 mt-4">
              Closing balance carries forward to next day's opening balance
            </p>
          </div>
        </div>
      )}

      {/* Adjustment Dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adjust Inventory - {formatDate(selectedDate)}</DialogTitle>
            <p className="text-sm text-slate-600">Set target values or make manual adjustments to match physical count</p>
          </DialogHeader>
          <form onSubmit={handleAdjustmentSubmit} className="space-y-4">
            <Tabs value={adjustmentData.adjustment_type} onValueChange={(v) => setAdjustmentData({...adjustmentData, adjustment_type: v})}>
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="closing" data-testid="tab-closing">Closing</TabsTrigger>
                <TabsTrigger value="opening" data-testid="tab-opening">Opening</TabsTrigger>
                <TabsTrigger value="production" data-testid="tab-production">Production</TabsTrigger>
                <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
                <TabsTrigger value="manual" data-testid="tab-manual">Manual +/-</TabsTrigger>
              </TabsList>

              {/* Set Target Value Tabs */}
              {['closing', 'opening', 'production', 'sales'].map((type) => (
                <TabsContent key={type} value={type} className="space-y-4">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">
                      Current {type.charAt(0).toUpperCase() + type.slice(1)} {type === 'closing' ? 'Balance' : type === 'opening' ? 'Balance' : ''}
                    </h4>
                    {dailyBalance && (
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>Large: <span className="font-semibold">{dailyBalance[type === 'closing' ? 'closing_balance' : type === 'opening' ? 'opening_balance' : type]?.large || 0}</span></div>
                        <div>Medium: <span className="font-semibold">{dailyBalance[type === 'closing' ? 'closing_balance' : type === 'opening' ? 'opening_balance' : type]?.medium || 0}</span></div>
                        <div>Small: <span className="font-semibold">{dailyBalance[type === 'closing' ? 'closing_balance' : type === 'opening' ? 'opening_balance' : type]?.small || 0}</span></div>
                        <div>Cracked: <span className="font-semibold">{dailyBalance[type === 'closing' ? 'closing_balance' : type === 'opening' ? 'opening_balance' : type]?.cracked || 0}</span></div>
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-sm mb-2">Set New Target Values (leave blank to keep current)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor={`${type}_target_large`}>Large Eggs</Label>
                        <Input
                          id={`${type}_target_large`}
                          type="number"
                          data-testid={`${type}-target-large`}
                          value={adjustmentData.target_large}
                          onChange={(e) => setAdjustmentData({ ...adjustmentData, target_large: e.target.value })}
                          placeholder="Enter target value"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${type}_target_medium`}>Medium Eggs</Label>
                        <Input
                          id={`${type}_target_medium`}
                          type="number"
                          data-testid={`${type}-target-medium`}
                          value={adjustmentData.target_medium}
                          onChange={(e) => setAdjustmentData({ ...adjustmentData, target_medium: e.target.value })}
                          placeholder="Enter target value"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${type}_target_small`}>Small Eggs</Label>
                        <Input
                          id={`${type}_target_small`}
                          type="number"
                          data-testid={`${type}-target-small`}
                          value={adjustmentData.target_small}
                          onChange={(e) => setAdjustmentData({ ...adjustmentData, target_small: e.target.value })}
                          placeholder="Enter target value"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`${type}_target_cracked`}>Cracked Eggs</Label>
                        <Input
                          id={`${type}_target_cracked`}
                          type="number"
                          data-testid={`${type}-target-cracked`}
                          value={adjustmentData.target_cracked}
                          onChange={(e) => setAdjustmentData({ ...adjustmentData, target_cracked: e.target.value })}
                          placeholder="Enter target value"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              ))}

              {/* Manual Adjustment Tab */}
              <TabsContent value="manual" className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-700">Enter positive or negative numbers to add/subtract from current balance</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="large_adj">Large Eggs (+/-)</Label>
                    <Input
                      id="large_adj"
                      type="number"
                      data-testid="manual-large-adj"
                      value={adjustmentData.large_adjustment}
                      onChange={(e) => setAdjustmentData({ ...adjustmentData, large_adjustment: e.target.value })}
                      placeholder="e.g., -5 or +10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="medium_adj">Medium Eggs (+/-)</Label>
                    <Input
                      id="medium_adj"
                      type="number"
                      data-testid="manual-medium-adj"
                      value={adjustmentData.medium_adjustment}
                      onChange={(e) => setAdjustmentData({ ...adjustmentData, medium_adjustment: e.target.value })}
                      placeholder="e.g., -5 or +10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="small_adj">Small Eggs (+/-)</Label>
                    <Input
                      id="small_adj"
                      type="number"
                      data-testid="manual-small-adj"
                      value={adjustmentData.small_adjustment}
                      onChange={(e) => setAdjustmentData({ ...adjustmentData, small_adjustment: e.target.value })}
                      placeholder="e.g., -5 or +10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cracked_adj">Cracked Eggs (+/-)</Label>
                    <Input
                      id="cracked_adj"
                      type="number"
                      data-testid="manual-cracked-adj"
                      value={adjustmentData.cracked_adjustment}
                      onChange={(e) => setAdjustmentData({ ...adjustmentData, cracked_adjustment: e.target.value })}
                      placeholder="e.g., -5 or +10"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div>
              <Label htmlFor="reason">Reason for Adjustment *</Label>
              <Textarea
                id="reason"
                data-testid="adjustment-reason"
                value={adjustmentData.reason}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
                placeholder="Physical count adjustment, breakage, correction, etc."
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="apply-adjustment-btn">
                Apply Adjustment
              </Button>
              <Button type="button" variant="outline" onClick={() => setAdjustOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Large (Crates)</th>
                <th>Large (Singles)</th>
                <th>Medium (Crates)</th>
                <th>Medium (Singles)</th>
                <th>Small (Crates)</th>
                <th>Small (Singles)</th>
                <th>Cracked</th>
                <th>Total Eggs</th>
                <th>Notes</th>
                {(user.role === 'admin' || user.role === 'manager') && <th>Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="production-table-body">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.date)}</td>
                  <td>{entry.large_crates || 0}</td>
                  <td>{entry.large_singles || 0}</td>
                  <td>{entry.medium_crates || 0}</td>
                  <td>{entry.medium_singles || 0}</td>
                  <td>{entry.small_crates || 0}</td>
                  <td>{entry.small_singles || 0}</td>
                  <td>{entry.cracked_eggs || 0}</td>
                  <td className="font-semibold">{calculateTotalEggs(entry)}</td>
                  <td className="max-w-xs truncate">{entry.notes || '-'}</td>
                  {(user.role === 'admin' || user.role === 'manager') && (
                  <td>
                    <div className="flex gap-2">
                      <button
                        data-testid={`edit-production-${entry.id}`}
                        onClick={() => handleEdit(entry)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-production-${entry.id}`}
                        onClick={() => handleDelete(entry.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No production entries yet. Add your first entry!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Production;
